import React from "react";
import {
  SCROLL_CONTAINER_GUARD_STYLE,
  mergeGuardStyle,
} from "../utils/width-guard";

export interface TableProps
  extends React.TableHTMLAttributes<HTMLTableElement> {
  children?: React.ReactNode;
  /**
   * Styles for the horizontal scroll wrapper that surrounds the table.
   * Merged over {@link SCROLL_CONTAINER_GUARD_STYLE}, so passing
   * `{ overflowX: "visible" }` opts a single table out of scrolling.
   */
  wrapperStyle?: React.CSSProperties;
  /**
   * Additional className for the horizontal scroll wrapper.
   */
  wrapperClassName?: string;
}

/**
 * Responsive table wrapper component.
 *
 * A GFM table's min-content width regularly exceeds a phone viewport. The table
 * keeps its natural width and scrolls horizontally *inside* this wrapper, so it
 * can never widen the article column (and therefore can never widen the page).
 *
 * The wrapper styles are inline on purpose - production sites are served a
 * safelist-compiled Tailwind build where an unextracted utility class has no
 * rule at all.
 */
export function Table({
  children,
  className,
  style,
  wrapperStyle,
  wrapperClassName,
  ...props
}: TableProps) {
  const tableClassName = className
    ? `markdown-table ${className}`
    : "markdown-table";

  return (
    <div
      className={
        wrapperClassName ? `table-wrapper ${wrapperClassName}` : "table-wrapper"
      }
      style={mergeGuardStyle(SCROLL_CONTAINER_GUARD_STYLE, wrapperStyle)}
    >
      <table className={tableClassName} style={style} {...props}>
        {children}
      </table>
    </div>
  );
}

/**
 * Table head component
 */
export function TableHead({
  children,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <thead {...props}>{children}</thead>;
}

/**
 * Table body component
 */
export function TableBody({
  children,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props}>{children}</tbody>;
}

/**
 * Table row component
 */
export function TableRow({
  children,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr {...props}>{children}</tr>;
}

/**
 * Table header cell component
 */
export function TableHeaderCell({
  children,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return <th {...props}>{children}</th>;
}

/**
 * Table data cell component
 */
export function TableDataCell({
  children,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td {...props}>{children}</td>;
}
